import { t } from "@travily/i18n";
import { tv, type VariantProps } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import { Link } from "@ui/primitives/Link";
import { createContext, type ReactNode, useContext } from "react";
import { ExitIcon } from "../../Icon/icons";
import IconAchievement from "./assets/icon-achievement.svg?react";
import IconError from "./assets/icon-error.svg?react";
import IconInfo from "./assets/icon-info.svg?react";
import IconSuccess from "./assets/icon-success.svg?react";
import IconWarning from "./assets/icon-warning.svg?react";

const feedback = tv({
  slots: {
    root: "relative flex w-full items-start gap-3 overflow-clip rounded-3xl",
    statusIcon: "relative shrink-0",
    content: "relative flex min-w-0 flex-1 flex-col",
    title: "font-semibold",
    description: "opacity-75",
    action: [
      "mt-1 self-start cursor-pointer rounded-sm font-bold",
      "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current",
    ],
    closeButton: [
      "absolute flex shrink-0 cursor-pointer items-center justify-center rounded-sm transition-opacity",
      "hover:opacity-100 pressed:opacity-75",
      "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current",
    ],
  },
  defaultVariants: {
    size: "md",
  },
  variants: {
    size: {
      md: {
        root: "p-4",
        statusIcon: "size-11",
        title: "text-xl/7",
        description: "text-base/6",
        action: "text-base/6",
        closeButton: "end-4 top-5 size-5",
      },
      sm: {
        root: "p-3",
        statusIcon: "size-8",
        content: "gap-1",
        title: "text-base/6",
        description: "text-sm/5",
        action: "text-sm/5",
        closeButton: "end-3 top-3.5 size-4",
      },
    },
    status: {
      success: {
        root: [
          "bg-green-50",
          "shadow-[var(--shadow-card),inset_0px_-1px_0px_0px_var(--color-green-200),inset_0px_0px_0px_1px_var(--color-green-100)]",
        ],
        title: "text-green-800",
        description: "text-green-700",
        action: "text-green-600 hover:text-green-700 pressed:text-green-800",
        closeButton: "text-green-600 hover:text-green-700 pressed:text-green-800",
      },
      error: {
        root: [
          "bg-pink-50",
          "shadow-[var(--shadow-card),inset_0px_-1px_0px_0px_var(--color-pink-200),inset_0px_0px_0px_1px_var(--color-pink-100)]",
        ],
        title: "text-pink-900 opacity-75",
        description: "text-pink-800",
        action: "text-pink-500 hover:text-pink-600 pressed:text-pink-700",
        closeButton: "text-pink-700 hover:text-pink-800 pressed:text-pink-900",
      },
      warning: {
        root: [
          "bg-orange-50",
          "shadow-[var(--shadow-card),inset_0px_-1px_0px_0px_var(--color-orange-200),inset_0px_0px_0px_1px_var(--color-orange-100)]",
        ],
        title: "text-orange-700",
        description: "text-orange-700",
        action: "text-orange-500 hover:text-orange-600 pressed:text-orange-700",
        closeButton: "text-orange-600 hover:text-orange-700 pressed:text-orange-800",
      },
      info: {
        root: [
          "bg-purple-50",
          "shadow-[var(--shadow-card),inset_0px_-1px_0px_0px_rgba(101,113,152,0.4),inset_0px_0px_0px_1px_var(--color-purple-300)]",
        ],
        title: "text-purple-600",
        description: "text-purple-600",
        action: "text-purple-500 hover:text-purple-600 pressed:text-purple-700",
        closeButton: "text-purple-500 hover:text-purple-600 pressed:text-purple-700",
      },
      achievement: {
        root: [
          "bg-yellow-50",
          "shadow-[var(--shadow-card),inset_0px_-1px_0px_0px_var(--color-yellow-500),inset_0px_0px_0px_1px_var(--color-yellow-400)]",
        ],
        title: "text-yellow-900",
        description: "text-yellow-900",
        action: "text-yellow-700 hover:text-yellow-800 pressed:text-yellow-900",
        closeButton: "text-yellow-800 hover:text-yellow-900 pressed:text-yellow-950",
      },
    },
    hasCloseButton: {
      true: { content: "pe-7" },
    },
  },
});

type FeedbackVariants = VariantProps<typeof feedback>;

const statusIcons = {
  success: IconSuccess,
  error: IconError,
  warning: IconWarning,
  info: IconInfo,
  achievement: IconAchievement,
} as const;

const statusRoles = {
  success: "status",
  error: "alert",
  warning: "alert",
  info: "status",
  achievement: "status",
} as const;

export type FeedbackStatus = NonNullable<FeedbackVariants["status"]>;

type FeedbackStyles = ReturnType<typeof feedback>;
const FeedbackContext = createContext<FeedbackStyles | null>(null);

interface FeedbackActionProps {
  href?: string;
  label: string;
  onPress?: () => void;
}

export type FeedbackSize = NonNullable<FeedbackVariants["size"]>;

interface FeedbackRootProps {
  children?: ReactNode;
  description: string | string[];
  onClose?: () => void;
  size?: FeedbackSize;
  status: FeedbackStatus;
  title: string;
}

function FeedbackRoot({ children, description, onClose, size, status, title }: FeedbackRootProps) {
  const styles = feedback({ size, status, hasCloseButton: !!onClose });
  const StatusIcon = statusIcons[status];
  const role = statusRoles[status];
  const closeIconSize = size === "sm" ? "size-4" : "size-5";

  return (
    <FeedbackContext value={styles}>
      <div className={styles.root()} role={role}>
        <StatusIcon aria-hidden="true" className={styles.statusIcon()} />
        <div className={styles.content()}>
          <p className={styles.title()}>{title}</p>
          {Array.isArray(description) ? (
            <ul className={styles.description({ className: "flex list-disc flex-col gap-0.5 ps-4" })}>
              {description.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : (
            <p className={styles.description()}>{description}</p>
          )}
          {children}
        </div>
        {onClose && (
          <Button aria-label={t("common.actions.close")} className={styles.closeButton()} onPress={onClose}>
            <ExitIcon className={closeIconSize} />
          </Button>
        )}
      </div>
    </FeedbackContext>
  );
}

function FeedbackAction({ href, label, onPress }: FeedbackActionProps) {
  const styles = useContext(FeedbackContext);
  if (!styles) throw new Error("Feedback.Action must be used within Feedback.Root");

  if (href) {
    return (
      <Link href={href} className={styles.action()}>
        {label}
      </Link>
    );
  }

  return (
    <Button className={styles.action()} onPress={onPress}>
      {label}
    </Button>
  );
}

export const Feedback = {
  Root: FeedbackRoot,
  Action: FeedbackAction,
};
